import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    TextField,
    Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { catalogApi } from 'api/catalog';
import { catalogCompanySourceMapApi } from 'api/inventory/catalogCompanySourceMap';
import { CustomerGuaranteeDaysByCompanySourceFields } from 'components/catalog/components/CustomerGuaranteeDaysByCompanySourceFields';
import {
    areAllCustomerGuaranteeDaysFilled,
    buildCustomerGuaranteeDaysByCompanySourcePayload,
    buildCustomerGuaranteeDaysChips,
    buildCustomerGuaranteeDaysPrefillFromAddResponses,
    getCompanySourceIDsNeedingCgdFromAddResponses,
    syncCustomerGuaranteeDaysByCompanySource,
} from 'components/catalog/utils/customerGuaranteeDaysByCompanySource';
import { ConfirmDialog } from 'shared/components/ConfirmDialog';
import {
    mapCompanySourcesToSelectOptions,
    useCompanySourceOptionsQuery,
} from 'shared/hooks/inventory/useCompanySourceOptionsQuery';

function formatPartialAddFailureMessage({ failedOptions, firstFailureMsg, succeededCount }) {
    const failedCount = failedOptions.length;
    return `${succeededCount} added; ${failedCount} failed (${
        firstFailureMsg || 'Failed to add company source'
    }). Set CGD for sources that need it, then retry the rest.`;
}

function formatRetryFailureMessage({ failedOptions, firstFailureMsg, succeededCount }) {
    const failedCount = failedOptions.length;
    return `${succeededCount} added; ${failedCount} failed (${
        firstFailureMsg || 'Failed to add company source'
    }). Retry the remaining sources.`;
}

function CompanySourcesCell({ entityType, onRefresh, row, setNotification }) {
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [addStep, setAddStep] = useState('select'); // 'select' | 'confirmCgd'
    const [selectedToAdd, setSelectedToAdd] = useState([]);
    const [sourcesNeedingCgd, setSourcesNeedingCgd] = useState([]);
    const [existingCgdFromAdd, setExistingCgdFromAdd] = useState([]);
    const [failedToRetry, setFailedToRetry] = useState([]);
    const [cgdByCompanySourceID, setCgdByCompanySourceID] = useState({});
    const [cgdError, setCgdError] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [pendingRemove, setPendingRemove] = useState(null);
    const [addedSourcesCount, setAddedSourcesCount] = useState(0);

    const requiresCgdOnAdd = entityType === 'externalSku';
    const isConfirmCgdStep = requiresCgdOnAdd && addStep === 'confirmCgd';

    const {
        data: catalogCompanySources = [],
        error: optionsError,
        isError: isOptionsError,
        isLoading: isOptionsLoading,
    } = useCompanySourceOptionsQuery();

    const companySourceOptions = useMemo(
        () => mapCompanySourcesToSelectOptions(catalogCompanySources),
        [catalogCompanySources],
    );

    const companySources = row.companySources ?? [];
    const assignedIds = new Set(companySources.map((source) => source.value));
    const availableOptions = companySourceOptions.filter((option) => !assignedIds.has(option.value));

    const existingCgdChips = requiresCgdOnAdd
        ? buildCustomerGuaranteeDaysChips({
              entries: row.customerGuaranteeDaysByCompanySource,
              companySources,
          })
        : [];

    const sourcesNeedingCgdIDs = sourcesNeedingCgd.map((option) => option.value);
    const sourcesNeedingCgdOptions = sourcesNeedingCgd.map((option) => ({
        id: option.value,
        displayName: option.text,
    }));

    const cgdFilledForSelection =
        sourcesNeedingCgdIDs.length > 0 &&
        areAllCustomerGuaranteeDaysFilled(cgdByCompanySourceID, sourcesNeedingCgdIDs);

    const failedRetryLabel = failedToRetry.map((option) => option.text).join(', ');
    const existingCgdFromAddChips = buildCustomerGuaranteeDaysChips({
        entries: existingCgdFromAdd,
        companySources: selectedToAdd,
    });

    const getRemoveConfirmContent = (item) => ({
        title: 'Remove company source?',
        description:
            entityType === 'packaging'
                ? `Remove "${item.text}" from packaging #${row.id}?`
                : `Remove "${item.text}" from external SKU ${row.externalSku}?`,
        confirmLabel: 'Remove',
    });

    const resetAddModalState = () => {
        setAddModalOpen(false);
        setAddStep('select');
        setSelectedToAdd([]);
        setSourcesNeedingCgd([]);
        setExistingCgdFromAdd([]);
        setFailedToRetry([]);
        setCgdByCompanySourceID({});
        setCgdError(false);
        setAddedSourcesCount(0);
    };

    const finishAddSuccess = (count) => {
        onRefresh();
        resetAddModalState();
        setNotification({
            text: count === 1 ? 'Company source added' : `${count} company sources added`,
            level: 'success',
        });
    };

    const returnToRetryFailed = ({ failedOptions, firstFailureMsg, succeededCount }) => {
        onRefresh();
        setFailedToRetry([]);
        setSourcesNeedingCgd([]);
        setExistingCgdFromAdd([]);
        setCgdByCompanySourceID({});
        setCgdError(false);
        setAddedSourcesCount(0);
        setSelectedToAdd(failedOptions);
        setAddStep('select');
        setNotification({
            text: formatRetryFailureMessage({
                succeededCount,
                failedOptions,
                firstFailureMsg,
            }),
            level: 'error',
        });
    };

    const closeAddModal = async () => {
        if (isSaving) {
            return;
        }

        // Confirm step already POSTed maps — cancel must revert those memberships.
        if (isConfirmCgdStep && selectedToAdd.length > 0) {
            setIsSaving(true);
            const rollbackResponses = await Promise.all(
                selectedToAdd.map((option) =>
                    catalogCompanySourceMapApi.removeExternalSkuCompanySource(row.externalSku, option.value),
                ),
            );
            setIsSaving(false);

            const rollbackFailed = rollbackResponses.some((response) => !response.status);
            onRefresh();
            resetAddModalState();
            setNotification({
                text: rollbackFailed
                    ? 'Add cancelled, but some company sources could not be reverted. Refresh and remove them if needed.'
                    : 'Add cancelled. Company source changes were reverted.',
                level: 'error',
            });
            return;
        }

        resetAddModalState();
    };

    const handleSelectedToAddChange = (value) => {
        setSelectedToAdd(value);
        setCgdError(false);
    };

    const promptForMissingCgd = ({
        addResponsesByCompanySourceID,
        failedOptions,
        firstFailureMsg,
        succeededOptions,
    }) => {
        const succeededIDs = succeededOptions.map((option) => option.value);
        const needingCgdIDs = getCompanySourceIDsNeedingCgdFromAddResponses({
            companySourceIDs: succeededIDs,
            addResponsesByCompanySourceID,
        });

        if (needingCgdIDs.length === 0) {
            if (failedOptions.length > 0) {
                returnToRetryFailed({
                    failedOptions,
                    succeededCount: succeededOptions.length,
                    firstFailureMsg,
                });
                return;
            }
            finishAddSuccess(succeededOptions.length);
            return;
        }

        const needingCgdIdSet = new Set(needingCgdIDs);
        const optionsNeedingCgd = succeededOptions.filter((option) => needingCgdIdSet.has(option.value));
        const prefillFromResponses = buildCustomerGuaranteeDaysPrefillFromAddResponses({
            companySourceIDs: succeededIDs,
            addResponsesByCompanySourceID,
        });
        const existingFromAddEntries = succeededOptions
            .filter((option) => !needingCgdIdSet.has(option.value))
            .map((option) => ({
                companySourceID: option.value,
                customerGuaranteeDays: Number(prefillFromResponses[String(option.value)]),
            }))
            .filter((entry) => Number.isFinite(entry.customerGuaranteeDays));

        // Keep all succeeded sources visible in the picker; only prompt CGD for missing ones.
        setSelectedToAdd(succeededOptions);
        setSourcesNeedingCgd(optionsNeedingCgd);
        setExistingCgdFromAdd(existingFromAddEntries);
        setFailedToRetry(failedOptions);
        setCgdByCompanySourceID(syncCustomerGuaranteeDaysByCompanySource({}, needingCgdIDs));
        setCgdError(false);
        setAddedSourcesCount(succeededOptions.length);
        setAddStep('confirmCgd');

        if (failedOptions.length > 0) {
            setNotification({
                text: formatPartialAddFailureMessage({
                    succeededCount: succeededOptions.length,
                    failedOptions,
                    firstFailureMsg,
                }),
                level: 'error',
            });
        }
    };

    const handleAddMaps = async () => {
        setIsSaving(true);

        const responses = await Promise.all(
            selectedToAdd.map((option) =>
                entityType === 'packaging'
                    ? catalogCompanySourceMapApi.addPackagingCompanySource(row.id, option.value)
                    : catalogCompanySourceMapApi.addExternalSkuCompanySource(row.externalSku, option.value),
            ),
        );

        const failedOptions = [];
        const succeededOptions = [];
        const addResponsesByCompanySourceID = {};

        responses.forEach((response, index) => {
            const option = selectedToAdd[index];
            if (response.status) {
                succeededOptions.push(option);
                addResponsesByCompanySourceID[option.value] = response;
            } else {
                failedOptions.push(option);
            }
        });

        setIsSaving(false);

        const firstFailureMsg = responses.find((response) => !response.status)?.msg;

        if (succeededOptions.length === 0) {
            setSelectedToAdd(failedOptions);
            setFailedToRetry([]);
            setNotification({
                text: firstFailureMsg || 'Failed to add company source',
                level: 'error',
            });
            return;
        }

        if (!requiresCgdOnAdd) {
            if (failedOptions.length === 0) {
                finishAddSuccess(succeededOptions.length);
                return;
            }
            returnToRetryFailed({
                failedOptions,
                succeededCount: succeededOptions.length,
                firstFailureMsg,
            });
            return;
        }

        promptForMissingCgd({
            succeededOptions,
            failedOptions,
            addResponsesByCompanySourceID,
            firstFailureMsg,
        });
    };

    const handleSaveCgd = async () => {
        if (!areAllCustomerGuaranteeDaysFilled(cgdByCompanySourceID, sourcesNeedingCgdIDs)) {
            setCgdError(true);
            return;
        }

        setIsSaving(true);
        const cgdPayload = buildCustomerGuaranteeDaysByCompanySourcePayload(cgdByCompanySourceID, sourcesNeedingCgdIDs);
        const cgdResponse = await catalogApi.upsertCustomerGuaranteeDaysByCompanySource(row.externalSku, cgdPayload);
        setIsSaving(false);

        if (!cgdResponse.status) {
            onRefresh();
            setNotification({
                text:
                    cgdResponse.msg ||
                    'Company source added, but failed to save customer guarantee days. Edit CGD for the new source.',
                level: 'error',
            });
            return;
        }

        if (failedToRetry.length > 0) {
            const succeededCount = addedSourcesCount || selectedToAdd.length;
            const failedOptions = failedToRetry;
            onRefresh();
            setFailedToRetry([]);
            setSourcesNeedingCgd([]);
            setExistingCgdFromAdd([]);
            setCgdByCompanySourceID({});
            setCgdError(false);
            setAddedSourcesCount(0);
            setSelectedToAdd(failedOptions);
            setAddStep('select');
            setNotification({
                text: `${succeededCount} added with CGD saved; ${failedOptions.length} still failed. Retry the remaining sources.`,
                level: 'error',
            });
            return;
        }

        finishAddSuccess(addedSourcesCount || selectedToAdd.length);
    };

    const handleRemove = async (item) => {
        const response = await (entityType === 'packaging'
            ? catalogCompanySourceMapApi.removePackagingCompanySource(row.id, item.value)
            : catalogCompanySourceMapApi.removeExternalSkuCompanySource(row.externalSku, item.value));

        if (response.status) {
            onRefresh();
            setNotification({ text: 'Company source removed', level: 'success' });
            return;
        }

        setNotification({
            text: response.msg || 'Failed to remove company source',
            level: 'error',
        });
    };

    const removeConfirmContent = pendingRemove ? getRemoveConfirmContent(pendingRemove) : null;

    return (
        <>
            {removeConfirmContent && (
                <ConfirmDialog
                    open
                    title={removeConfirmContent.title}
                    description={removeConfirmContent.description}
                    confirmLabel={removeConfirmContent.confirmLabel}
                    confirmVariant="destructive"
                    onCancel={() => setPendingRemove(null)}
                    onConfirm={async () => {
                        await handleRemove(pendingRemove);
                        setPendingRemove(null);
                    }}
                />
            )}

            <Dialog open={addModalOpen} fullWidth maxWidth="sm" onClose={closeAddModal}>
                <DialogContent>
                    <h4>{isConfirmCgdStep ? 'Finish adding company sources' : 'Add company sources for this item'}</h4>
                    {isOptionsError && (
                        <p style={{ color: '#d32f2f', marginBottom: 16 }}>
                            {optionsError?.message || 'Failed to load company source options'}
                        </p>
                    )}

                    {!isConfirmCgdStep ? (
                        <>
                            {isOptionsLoading ? (
                                <CircularProgress size={24} />
                            ) : (
                                <Autocomplete
                                    id="CompanySourcesCell-Autocomplete"
                                    multiple
                                    options={availableOptions}
                                    value={selectedToAdd}
                                    onChange={(event, value) => handleSelectedToAddChange(value)}
                                    getOptionLabel={(option) => option.text}
                                    disabled={isOptionsError || isSaving}
                                    style={{ width: '100%' }}
                                    renderInput={(params) => (
                                        <TextField {...params} variant="outlined" label="Company sources" />
                                    )}
                                />
                            )}

                            {requiresCgdOnAdd ? (
                                <Box mt={2}>
                                    <Typography variant="subtitle2">Currently on this item</Typography>
                                    <Typography variant="caption" color="textSecondary" display="block">
                                        Company sources already mapped, with any CGD already set.
                                    </Typography>
                                    <Box mt={1} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                                        {existingCgdChips.length > 0 ? (
                                            existingCgdChips.map((chip) => (
                                                <Chip
                                                    key={chip.companySourceID}
                                                    size="small"
                                                    color="primary"
                                                    label={chip.label}
                                                />
                                            ))
                                        ) : (
                                            <Typography variant="caption" color="textSecondary">
                                                No CGD set yet for existing company sources.
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ) : null}
                        </>
                    ) : (
                        <Box>
                            <Box
                                mb={2}
                                p={1.5}
                                style={{
                                    backgroundColor: '#fafafa',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 4,
                                }}
                            >
                                <Typography variant="subtitle2">1. Currently on this item</Typography>
                                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                    Already mapped before this add. Unchanged by cancel.
                                </Typography>
                                <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                                    {existingCgdChips.length > 0 ? (
                                        existingCgdChips.map((chip) => (
                                            <Chip
                                                key={chip.companySourceID}
                                                size="small"
                                                variant="outlined"
                                                label={chip.label}
                                            />
                                        ))
                                    ) : (
                                        <Typography variant="caption" color="textSecondary">
                                            None with CGD set.
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            <Box
                                mb={2}
                                p={1.5}
                                style={{
                                    backgroundColor: '#e3f2fd',
                                    border: '1px solid #90caf9',
                                    borderRadius: 4,
                                }}
                            >
                                <Typography variant="subtitle2">2. Newly added — CGD already set</Typography>
                                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                    Just mapped; surviving CGD found. No entry needed.
                                </Typography>
                                <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                                    {existingCgdFromAddChips.length > 0 ? (
                                        existingCgdFromAddChips.map((chip) => (
                                            <Chip
                                                key={chip.companySourceID}
                                                size="small"
                                                color="primary"
                                                label={chip.label}
                                            />
                                        ))
                                    ) : (
                                        <Typography variant="caption" color="textSecondary">
                                            None in this add.
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            <Box
                                mb={2}
                                p={1.5}
                                style={{
                                    backgroundColor: '#fff8e1',
                                    border: '1px solid #ffe082',
                                    borderRadius: 4,
                                }}
                            >
                                <Typography variant="subtitle2">3. Newly added — CGD required</Typography>
                                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                    Just mapped with no CGD yet. Enter values to finish, or cancel to undo the add.
                                </Typography>
                                <CustomerGuaranteeDaysByCompanySourceFields
                                    companySourceIDs={sourcesNeedingCgdIDs}
                                    companySourceOptions={sourcesNeedingCgdOptions}
                                    valuesByCompanySourceID={cgdByCompanySourceID}
                                    onChange={(next) => {
                                        setCgdByCompanySourceID(next);
                                        setCgdError(false);
                                    }}
                                    disabled={isSaving}
                                    showError={cgdError}
                                />
                            </Box>

                            {failedToRetry.length > 0 ? (
                                <Box mb={2}>
                                    <Typography variant="body2" color="error">
                                        {`Could not add (retry after save): ${failedRetryLabel}.`}
                                    </Typography>
                                </Box>
                            ) : null}

                            <Typography variant="caption" color="textSecondary" display="block" paragraph>
                                Closing or cancelling reverts the newly added company sources from this session.
                            </Typography>
                        </Box>
                    )}

                    <Divider />

                    <Box mt={2} display="flex" style={{ gap: 8 }}>
                        {isConfirmCgdStep ? (
                            <>
                                <Button
                                    id="CompanySourcesCell-CancelCgd"
                                    variant="outlined"
                                    disabled={isSaving}
                                    onClick={closeAddModal}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    id="CompanySourcesCell-SaveCgd"
                                    variant="contained"
                                    color="primary"
                                    disabled={isSaving || !cgdFilledForSelection}
                                    onClick={handleSaveCgd}
                                >
                                    {isSaving ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        'Save customer guarantee days'
                                    )}
                                </Button>
                            </>
                        ) : (
                            <Button
                                id="CompanySourcesCell-Add"
                                variant="outlined"
                                disabled={selectedToAdd.length === 0 || isOptionsLoading || isOptionsError || isSaving}
                                onClick={handleAddMaps}
                            >
                                {isSaving ? <CircularProgress size={16} color="inherit" /> : 'Add Company Sources'}
                            </Button>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>

            <div style={{ textAlign: 'center' }}>
                {companySources.map((item) => (
                    <Chip
                        key={item.id}
                        size="small"
                        label={item.text}
                        color="primary"
                        style={{ margin: '2px 1px' }}
                        onDelete={() => setPendingRemove(item)}
                    />
                ))}
                <Chip
                    size="small"
                    clickable={!isOptionsLoading}
                    color="secondary"
                    style={{ margin: '2px' }}
                    label={isOptionsLoading ? '…' : '+'}
                    onClick={(event) => {
                        event.stopPropagation();
                        if (isOptionsLoading) {
                            return;
                        }
                        setSelectedToAdd([]);
                        setSourcesNeedingCgd([]);
                        setExistingCgdFromAdd([]);
                        setFailedToRetry([]);
                        setCgdByCompanySourceID({});
                        setCgdError(false);
                        setAddedSourcesCount(0);
                        setAddStep('select');
                        setAddModalOpen(true);
                    }}
                />
            </div>
        </>
    );
}

/**
 * Stable DataTable cell renderer. Props are passed via column.companySourceCellProps
 * so react-table does not treat a new component type on every parent render.
 */
function CompanySourcesTableCell({ column, row }) {
    const { entityType, onRefresh, setNotification } = column.companySourceCellProps ?? {};

    return (
        <CompanySourcesCell
            row={row.original}
            entityType={entityType}
            onRefresh={onRefresh}
            setNotification={setNotification}
        />
    );
}

export { CompanySourcesCell, CompanySourcesTableCell };
